import { useDisclosure } from '@chakra-ui/react';
import {
  AggregationModelEnum,
  BillGroupSummary,
  IAccountRespSchema,
  IGetSubscriptionItemSchema,
  IGetSubscriptionSchema,
  IUsageTypeResSchema,
  ProductTypeEnum,
  SubscriptionBillingStatusEnum,
} from '@monetize/types/app';
import { Maybe } from '@monetize/types/common';
import { orderObjectsBy, toDateShort } from '@monetize/utils/core';
import { useQueryClient } from '@tanstack/react-query';
import {
  AppColumnDef,
  ExpandedState,
  createColumnHelper,
} from '../../../components/Monetize/DataTableNew/appTable';
import React, { useCallback, useMemo, useState } from 'react';
import { MdExpandLess, MdExpandMore } from 'react-icons/md';
import { useNavigate } from 'react-router';
import { subscriptionServiceQueryKeys } from '../../../api/queryKeysService';
import { useGetUsageConsumption } from '../../../api/subscriptionsService';
import {
  MBox,
  MCustomIconButton,
  MFlex,
  MText,
} from '../../../components/Monetize';
import DataTableActions from '../../../components/Monetize/DataTable/MDataTableActions';
import { DataTable } from '../../../components/Monetize/DataTableNew/DataTable';
import { ACTION_COLUMN_KEY } from '../../../components/Monetize/DataTableNew/dataTableNewRenderers';
import {
  getColHeaderWithSize,
  idBodyTemplate,
  nameWithIdBodyTemplate,
  textBodyTemplate,
} from '../../../components/Monetize/DataTableNew/tableUtilsNew';
import { RefreshTableButton } from '../../../components/Monetize/RefreshTableButton';
import { ROUTES } from '../../../constants';
import { getBillGroupPageRoute } from '../../../constants/routes';
import { SubscriptionBillingStatusDisplayText } from '../../../constants/subscriptions';
import { useSubscriptions } from '../../../hooks/AccountDetail';
import { ManualUsageForm } from '../Subscriptions/Usage/ManualUsageForm';
import { UsageOverviewChartDrawer } from '../Subscriptions/Usage/UsageOverviewChartDrawer';
import { UsagePageHeader } from './components/UsagePageHeader';
import { UsageTypeIDName } from './components/UsageTypeIDName';
import { UsageUnit } from './components/UsageUnit';

interface SubscriptionRowData {
  id: string;
  name: string;
  description: string;
  status: SubscriptionBillingStatusEnum;
  usagePeriod: string;
  billGroup: BillGroupSummary;
  subscriptionItems: SubscriptionItemRowData[];
  subscription: IGetSubscriptionSchema;
}

interface SubscriptionItemRowData {
  id: string;
  subscriptionId: string;
  usagePeriodStart: string;
  usagePeriodEnd: string;
  name: string;
  description: Maybe<string>;
  productId: string;
  usageTypes: IUsageTypeResSchema[];
  subscriptionItem: IGetSubscriptionItemSchema;
}

const activeStatuses = new Set([
  SubscriptionBillingStatusEnum.ACTIVE,
  SubscriptionBillingStatusEnum.PENDING,
]);

const columnHelper = createColumnHelper<SubscriptionRowData>();

const UnitColumn = ({ row }: { row: SubscriptionItemRowData }) => {
  const {
    data: consumption,
    isError,
    isLoading,
  } = useGetUsageConsumption(
    {
      subscriptionId: row.subscriptionId,
      usageTypeIds: row.usageTypes.map((type) => type.id),
      startTime: `${row.usagePeriodStart}Z`,
      endTime: `${row.usagePeriodEnd}Z`,
      aggregationModel: AggregationModelEnum.SUM,
    },
    {
      refetchOnMount: false,
      // keep data available for 25 minutes
      gcTime: 25 * 60 * 1000, // 25 minutes
      // don't refresh data within a 5 minute window
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  );

  const unitsConsumed = consumption
    ? consumption.unitsConsumed + consumption.ratedUnitsConsumed
    : undefined;

  return (
    <UsageUnit
      consumptionLoading={isLoading}
      hasError={isError}
      usageTypes={row.usageTypes}
      unitsConsumed={unitsConsumed}
    />
  );
};

const getColumnsBase = (
  showBillGroupColumn: boolean,
  actionBodyTemplate: (
    rowData: SubscriptionRowData | SubscriptionItemRowData,
    isSubscription: boolean,
    parentRow: Maybe<SubscriptionRowData>,
  ) => React.ReactNode,
): AppColumnDef<any, any>[] => [
  columnHelper.accessor('name', {
    ...getColHeaderWithSize('Name', 300),
    cell: ({ row, getValue }) => {
      const name = getValue();
      const rowData = row.original;
      const isExpanded = row.getIsExpanded();

      return (
        <MFlex gap={2} align="center">
          {row.depth === 0 && (
            <MCustomIconButton
              icon={isExpanded ? MdExpandLess : MdExpandMore}
              btnSize={5}
              onClick={(event) => {
                event.stopPropagation();
                row.toggleExpanded();
              }}
              _focus={{
                bg: 'transparent',
              }}
              aria-label={
                isExpanded ? `Collapse row ${row.id}` : `Expand row ${row.id}`
              }
            />
          )}
          <MBox ml={row.depth > 0 ? 7 : 0}>
            <MText fontWeight="500" wordBreak="break-word">
              {name}
            </MText>
            <MText color="tGray.darkPurple" fontSize="xs">
              {rowData.usagePeriod}
            </MText>
            {rowData.status !== SubscriptionBillingStatusEnum.ACTIVE && (
              <MText color="tRed.base" fontSize="xs">
                {SubscriptionBillingStatusDisplayText[rowData.status]}
              </MText>
            )}
          </MBox>
        </MFlex>
      );
    },
  }),

  // TODO: optional bill group column if we are not scoped to a bill group
  ...(showBillGroupColumn
    ? [
        // {
        //   field: 'billGroupId',
        //   header: 'Bill Group',
        //   style: { width: '10rem' },
        //   body: nameWithIdBodyTemplate<
        //     ISubscriptionWithBillGroup,
        //     BillGroupSummary
        //   >('billGroup', { fallbackProperty: 'billGroupId' }),
        // },
        columnHelper.accessor('billGroup', {
          ...getColHeaderWithSize('Bill Groups', 300),
          cell: (data) => {
            if (data.row.depth !== 0) {
              return null;
            }

            return nameWithIdBodyTemplate<SubscriptionRowData, unknown>({
              idProp: 'billGroup.id' as keyof SubscriptionRowData,
              nameProp: 'billGroup.name' as keyof SubscriptionRowData,
              idLinkFn: (id) => getBillGroupPageRoute(id),
            })(data);
          },
        }),
      ]
    : []),

  columnHelper.accessor('id', {
    ...getColHeaderWithSize('Subscription Id', 250),
    cell: (data) => {
      const rowData = data.row.original as
        | SubscriptionRowData
        | SubscriptionItemRowData;
      if (data.row.depth === 0) {
        return idBodyTemplate<SubscriptionRowData, any>()(data);
      }
      return (
        <UsageTypeIDName
          usageTypes={(rowData as SubscriptionItemRowData).usageTypes}
          target="usageTypeId"
        />
      );
    },
  }),
  columnHelper.accessor('description', {
    ...getColHeaderWithSize('Description', 300),
    cell: textBodyTemplate(),
  }),
  {
    accessorKey: 'units',
    header: 'Units',
    accessorFn: (row) => '',
    cell: ({ row }) => {
      const rowData = row.original as
        | SubscriptionRowData
        | SubscriptionItemRowData;
      if (row.depth === 0) {
        return null;
      }
      return <UnitColumn row={rowData as SubscriptionItemRowData} />;
    },
  },
  columnHelper.accessor('subscriptionItems.usageTypes', {
    ...getColHeaderWithSize('Type', 150),
    cell: ({ row }) => {
      const rowData = row.original as
        | SubscriptionRowData
        | SubscriptionItemRowData;
      if (row.depth === 0) {
        return null;
      }
      return (
        <UsageTypeIDName
          usageTypes={(rowData as SubscriptionItemRowData).usageTypes}
          target="usageTypeName"
        />
      );
    },
  }),
  {
    id: ACTION_COLUMN_KEY,
    header: '',
    cell: ({ row }) => {
      return actionBodyTemplate(
        row.original,
        row.depth === 0,
        row.getParentRow()?.original as Maybe<SubscriptionRowData>,
      );
    },
    size: 75,
    enableSorting: false,
    enablePinning: true,
    enableResizing: false,
    meta: {
      cellStyles: {
        justifyContent: 'center',
      },
    },
  },
];

export const UsagePage: React.FC<{
  account: IAccountRespSchema;
  billGroupId?: string;
}> = ({ account, billGroupId }) => {
  const navigate = useNavigate();
  const { id: accountId } = account;
  const { listData, loading, refetch } = useSubscriptions({
    accountId,
    billGroupId,
    fetchAll: true,
  });
  const [showInactiveSubscriptions, setShowInactiveSubscriptions] =
    useState(false);

  const [expanded, setExpanded] = useState<ExpandedState>(true);

  const {
    open: isUsageChartsOpen,
    onOpen: onOpenUsageCharts,
    onClose: onCloseUsageCharts,
  } = useDisclosure();

  const {
    open: isUsageFormOpen,
    onOpen: onOpenUsageForm,
    onClose: onCloseUsageForm,
  } = useDisclosure();

  const { rows, hasInactiveSubscriptions } = useMemo(() => {
    const usageSubscriptions = orderObjectsBy(
      listData.content.filter((item) =>
        item.subscriptionItems.some(
          (subItem) => subItem.product.productType === ProductTypeEnum.USAGE,
        ),
      ),
      ['offeringName'],
    );

    const visibleSubscriptions = usageSubscriptions
      .filter((item) => {
        if (showInactiveSubscriptions) {
          return true;
        }
        return activeStatuses.has(item.billingStatus);
      })
      .map((subscription): SubscriptionRowData => {
        return {
          id: subscription.id,
          description: subscription.description,
          name: subscription.offering.name,
          status: subscription.billingStatus,
          usagePeriod: `${toDateShort(subscription.usagePeriodStart)} - ${toDateShort(subscription.usagePeriodEnd)}`,
          billGroup: subscription.billGroup,
          subscription,
          subscriptionItems: orderObjectsBy(
            subscription.subscriptionItems
              .filter(
                ({ product }) => product.productType === ProductTypeEnum.USAGE,
              )
              .map((subscriptionItem) => ({
                id: subscriptionItem.id,
                subscriptionId: subscription.id,
                usagePeriodStart: subscription.usagePeriodStart!,
                usagePeriodEnd: subscription.usagePeriodEnd!,
                name: subscriptionItem.product.name,
                description: subscriptionItem.description,
                productId: subscriptionItem.product.id,
                usageTypes: subscriptionItem.product.usageTypes || [],
                subscriptionItem,
              })),
            ['name'],
          ),
        };
      });
    return {
      hasInactiveSubscriptions: usageSubscriptions.some(
        (item) => !activeStatuses.has(item.billingStatus),
      ),
      rows: visibleSubscriptions,
    };
  }, [listData.content, showInactiveSubscriptions]);

  const actionBodyTemplate = useCallback(
    (
      rowData: SubscriptionRowData | SubscriptionItemRowData,
      isSubscription: boolean,
      parentRow: Maybe<SubscriptionRowData>,
    ) => {
      if (isSubscription) {
        const row = rowData as SubscriptionRowData;
        const actions = [
          {
            title: 'View Trend',
            enabled: true,
            action: () => {
              setSelectedSubscription(row);
              onOpenUsageCharts();
            },
          },
        ];
        return <DataTableActions actions={actions} />;
      }
      const row = rowData as SubscriptionItemRowData;
      const actions = [
        {
          title: 'Add Usage',
          enabled: true,
          action: () => {
            if (parentRow) {
              setSelectedSubscription(parentRow);
            }
            setSelectedSubscriptionItem(row);
            onOpenUsageForm();
          },
        },
        {
          title: 'View Trend',
          enabled: true,
          action: () => {
            if (parentRow) {
              setSelectedSubscription(parentRow);
            }
            setSelectedSubscriptionItem(row);
            onOpenUsageCharts();
          },
        },
        {
          title: 'Review Events',
          enabled: true,
          action: () => {
            const generatedRoute = ROUTES.getUsageEventsPageRoute(
              accountId,
              row.subscriptionId,
              row.productId,
            );
            navigate(generatedRoute);
          },
        },
      ];
      return <DataTableActions actions={actions} />;
    },
    [accountId, navigate, onOpenUsageCharts, onOpenUsageForm],
  );

  const columns = useMemo(
    () => getColumnsBase(!billGroupId, actionBodyTemplate),
    [actionBodyTemplate, billGroupId],
  );

  const [selectedSubscription, setSelectedSubscription] =
    useState<SubscriptionRowData>();
  const [selectedSubscriptionItem, setSelectedSubscriptionItem] =
    useState<SubscriptionItemRowData>();

  const queryClient = useQueryClient();

  const toggleExpandAll = () => {
    setExpanded((prev) =>
      prev === true || rows.every((value) => prev[value.id] === true)
        ? {}
        : true,
    );
  };

  const handleUsageUploadOnClose = () => {
    if (selectedSubscription) {
      // Invalidate all usageConsumption queries for the selected subscription
      selectedSubscription.subscriptionItems.forEach((item) => {
        queryClient.invalidateQueries({
          queryKey: subscriptionServiceQueryKeys.subscriptionUsageConsumption({
            subscriptionId: selectedSubscription.id,
            productId: item.productId,
            usageTypeIds: item.usageTypes?.map((type) => type.id) || [],
            startTime: selectedSubscription.subscription.usagePeriodStart ?? '',
            endTime: selectedSubscription.subscription.usagePeriodEnd ?? '',
            aggregationModel: AggregationModelEnum.SUM,
          }),
        });
      });
    }
    setSelectedSubscription(undefined);
    setSelectedSubscriptionItem(undefined);
    onCloseUsageForm();
  };

  const handleProductChartClose = () => {
    setSelectedSubscription(undefined);
    setSelectedSubscriptionItem(undefined);
    onCloseUsageCharts();
  };

  const isAllExpanded =
    expanded === true ||
    (Object.keys(expanded).length === rows.length &&
      rows.every((row) => expanded[row.id] === true));

  return (
    <MBox>
      <UsagePageHeader
        hideHeader={loading || rows.length === 0}
        isExpandedAll={isAllExpanded}
        onToggleExpandAll={toggleExpandAll}
        showInactiveSubscriptions={showInactiveSubscriptions}
        onToggleShowInactive={() =>
          setShowInactiveSubscriptions(!showInactiveSubscriptions)
        }
        showToggle={hasInactiveSubscriptions}
      >
        <RefreshTableButton refetch={refetch} />
      </UsagePageHeader>

      <DataTable
        columns={columns}
        data={rows}
        enablePagination={false}
        onRowClick={(
          row: SubscriptionRowData | SubscriptionItemRowData,
          tableRow,
        ) => {
          if (tableRow.depth === 0) {
            tableRow.toggleExpanded();
            return;
          }
          if ('subscriptionId' in row && 'productId' in row) {
            navigate(
              ROUTES.getUsageEventsPageRoute(
                accountId,
                row.subscriptionId,
                row.productId,
              ),
            );
          }
        }}
        isLoading={loading}
        emptyProps={{
          mainMessage: 'No Usage products on this account',
          smallMessage:
            'If any active contracts have usage products, they will show up here',
        }}
        state={{ expanded }}
        getSubRows={(row: SubscriptionRowData) => row.subscriptionItems}
        onExpandedChange={setExpanded}
      />

      {isUsageChartsOpen && selectedSubscription && (
        <UsageOverviewChartDrawer
          open={isUsageChartsOpen}
          onClose={handleProductChartClose}
          accountId={accountId as string}
          subscriptionId={selectedSubscription.id}
          subscription={selectedSubscription.subscription}
          singleProductId={selectedSubscriptionItem?.productId}
        />
      )}
      {isUsageFormOpen && selectedSubscription && selectedSubscriptionItem && (
        <ManualUsageForm
          isOpen={isUsageFormOpen}
          onClose={handleUsageUploadOnClose}
          subscriptionId={selectedSubscription.id}
          periodStartDate={selectedSubscription.subscription.periodStartDate!}
          usageTypes={selectedSubscriptionItem.usageTypes}
          accountId={accountId}
        />
      )}
    </MBox>
  );
};
